import React, { ReactNode } from "react";
import {
  Pressable,
  StyleSheet,
  View,
  type PressableProps,
  type StyleProp,
  type ViewStyle
} from "react-native";
import { colors } from "@/constants";

/**
 * Props for the Interactive primitive
 */
interface InteractiveProps extends Omit<
  PressableProps,
  "style" | "children" | "disabled"
> {
  /** Content rendered inside the pressable area */
  children: ReactNode;
  /** Base style applied to the pressable (layout, background, radius, etc.) */
  style?: StyleProp<ViewStyle>;
  /** When true, taps are ignored and the content is visually muted */
  disabled?: boolean;
  /** When true, renders the sticky pressed state (used for "current selection" affordance) */
  selected?: boolean;
}

/**
 * Design-system interactive primitive.
 *
 * Wraps a React Native Pressable with uniform hover, pressed, and disabled
 * state feedback using translucent overlays. The overlay is orthogonal to
 * base styling — one token set covers every interactive element regardless
 * of its background color.
 *
 * Leaf components (TextButton, IconButton, HeaderButton, Pill, etc.) should
 * wrap their content in this primitive rather than using Pressable or
 * TouchableOpacity directly.
 *
 * State treatment:
 * - Hover (web only): translucent dark overlay
 * - Pressed: stronger translucent dark overlay
 * - Disabled: muted opacity, no hover/press feedback
 *
 * @param props - Component props, see InteractiveProps
 */
export default function Interactive({
  children,
  style,
  disabled = false,
  selected = false,
  accessibilityRole = "button",
  ...rest
}: InteractiveProps) {
  return (
    <Pressable
      disabled={disabled}
      accessibilityRole={accessibilityRole}
      accessibilityState={{ disabled, selected }}
      style={[
        styles.container,
        style,
        disabled && { opacity: colors.disabledOpacity }
      ]}
      {...rest}
    >
      {({ hovered, pressed }) => {
        const showOverlay = !disabled && (selected || hovered || pressed);
        const overlayColor =
          pressed || selected
            ? colors.statePressedLayer
            : colors.stateHoverLayer;
        return (
          <>
            {children}
            {showOverlay && (
              <View
                pointerEvents="none"
                style={[
                  StyleSheet.absoluteFill,
                  { backgroundColor: overlayColor }
                ]}
              />
            )}
          </>
        );
      }}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  // Clips the state overlay to the component's borderRadius so the overlay
  // follows the shape of the interactive surface.
  container: {
    overflow: "hidden"
  }
});
